import { useCallback, useState } from 'react';
import PublicAPI from '../../../../../api/TreedisPublicDAO';
import { AIMessageRole } from '../enum';

export interface TAIMedia {
  url?: string;
  type?: string;
  fileName?: string;
  contentType?: string;
  fileSize?: number;
  uploadPath?: string;
}

export interface TAIMediaAttachment {
  id: number;
  messageId: number;
  fileName: string;
  mediaType: string;
  createdAt: string;
  presignedUrl: string;
  downloadUrl: string;
  presignedDownloadUrl?: string;
}

export interface TAIMessage {
  id?: number;
  conversationId?: number;
  role: AIMessageRole;
  content: string;
  media?: TAIMedia[];
  mediaAttachments?: TAIMediaAttachment[];
  createdAt?: string;
  timestamp?: number;
}

export interface TAIConversation {
  id?: number;
  tourSlug?: string;
  tourId: number;
  sessionId: string;
  message?: TAIMessage;
  createdAt?: number;
  updatedAt?: number;
}

export interface CreateConversationRequest extends TAIConversation {
}

export interface SendMessageRequest {
  role?: AIMessageRole;
  content: string;
  media?: TAIMedia[];
}

export interface SendMessageResponse {
  id: number;
  conversationId: number;
  role: AIMessageRole;
  content: string;
  createdAt: string;
  mediaAttachments?: TAIMediaAttachment[];
}

export interface CreateConversationResponse {
  id: number;
  tourId: number;
  sessionUuid: string;
  fingerprint: string;
  title: string | null;
  lastMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
  messages: TAIMessage[];
}

export function useTAIChat() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createTAIConversation = useCallback(async (request: CreateConversationRequest): Promise<CreateConversationResponse | null> => {
    setLoading(true);
    setError(null);

    try {
      const response = await PublicAPI.createTAIConversation({
        data: {
          ...request,
          message: {
            ...request.message,
            media: request.message?.media || []
          }
        }
      }, {
        isFingerprinted: true
      });

      if (!response || !response.data) {
        throw new Error('Invalid response from server');
      }

      return response.data as CreateConversationResponse;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to create conversation');
      setError(error);
      console.error('Error creating TAI conversation:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const sendTAIMessage = useCallback(async (conversationId: number, request: SendMessageRequest): Promise<SendMessageResponse | null> => {
    setLoading(true);
    setError(null);

    try {
      const response = await PublicAPI.sendTAIMessage(conversationId, {
        data: {
          ...request,
          media: request.media || []
        }
      }, {
        isFingerprinted: true
      });

      if (!response || !response.data) {
        throw new Error('Invalid response from server');
      }

      return response.data as SendMessageResponse;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to send message');
      setError(error);
      console.error('Error sending TAI message:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    createTAIConversation,
    sendTAIMessage,
    loading,
    error,
  };
}

